/**
 * @file   src\Pages\AddEquipment.tsx
 * @brief  AddEquipment page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { 
    React,
    useState,
    useEffect, 
    useRef, 
    useOutletContext, 
    Container, 
    Button, 
    Breadcrumb, 
    Row, 
    Col, 
    useNavigate, 
    Modal, 
    useParams, 
    useLocation 
} from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import CWSelect from '../components/CWSelect';
import CWInput from '../components/CWInput';
import { pageURLs } from '../utils/pageUrls';
import { AppContextType } from '../interfaces/generalInterface';
import *as Yup from 'yup'
import { EquipmentData } from '../interfaces/generalInterface';
import {  DEVICE_ID_MAX_LENGTH, EQUIPMENT_NAME_MAX_LENGTH, WASH_NUMBER_MAX_LENGTH } from '../utils/constants';
import {
    ConnectivityType,
    EquipmentType,
    equipmentTypeHidesPulse,
    equipmentTypeSupportsLpr,
} from '../utils/enums';
import { equipmentInfoSchema } from '../validations/equipmentInfoSchema';
import { LocationApi, EquipmentApi,CompanyApi } from "../utils/apiUrls";
import { MessageToaster } from "../utils/toastUtil";
import axios from '../config/axiosConfig';
import SmallLoader from '../components/SmallLoader';
import { userType } from '../utils/enums';
import { DeviceOption, DeviceItem } from '../interfaces/CarWashInterface';
import CWCheckbox from '../components/CWCheckbox';
import CWSwitch from '../components/CWSwitch';

const AddEquipment = (props:any) => {
    const [loading, setLoading] = useState(false);
    const param = useParams();
    const current_carwash_id= param.washid ? param.washid : 0;
    const current_location_id  =  param.id ? param.id : 0;
    const equipment_id = param.equipment_id ? param.equipment_id : 0;

    const navigate = useNavigate();
    const toaster = new MessageToaster();
    const [showEquipmentSuccesspoup, setShowEquipmentSuccesspoup] = useState<boolean>(false);
    const [deviceOptions, setdeviceOptions] = useState<DeviceOption[]>([]);
    const [categoryOptions, setCategoryOptions] = useState<DeviceOption[]>([]);

    const location = useLocation();
    const page = location.state?.page || 1;
    
    // Use a ref to store input elements
    const formRef = useRef<HTMLFormElement>(null);
    const [locationAllowsComboWash, setLocationAllowsComboWash] = useState(false);

    const isComboWashEnabled = (value: any) => value === 1 || value === true || value === "1";

    const equipmentTypeSupportsComboWash = (type: number) =>
        type === EquipmentType['In Bay'] || type === EquipmentType['Tunnel'];

    // State to company details
    const [company, setCompany] = useState({
        companyName: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipCode: ""
    });
    const [current_locationName, setCurrent_locationName] = useState('');

    // To get company details
    const getCompanyAddress = async () => {
        try {
            const param3 = `${current_carwash_id}`;
            const { data } = (props.userType !== userType['CARWASH-OWNER']) ? await axios.get(CompanyApi.GET_COMPANY_ADDRESS + param3) : await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS);
            if (data?.error === 0) {
                setCompany(data.data);
            }
        } catch (error: any) {
            console.error('Error fetching company information:', error.message);
            // throw new Error(error.response?.data?.message || error.message);
        }
    }
    // Function to fetch company information
    const getLocation = async () => {
        try {
            const param = `${current_location_id}`;
            const { data } = (props.userType !== userType['CARWASH-OWNER']) ? await axios.get(LocationApi.GET_SINGLE_LOCATION + param) : await axios.get(LocationApi.CARWASH_OWNER_LOCATION_DETAILS + param);
            setCurrent_locationName(data.data.locationName);
            const comboWashEnabled = isComboWashEnabled(data?.data?.comboWash);
            setLocationAllowsComboWash(comboWashEnabled);
            setFormData((prev) => ({
                ...prev,
                isComboWash: comboWashEnabled && equipmentTypeSupportsComboWash(prev.equipmenttype),
                ...(!comboWashEnabled ? { washCategoryId: null } : {}),
            }));

        } catch (error: any) {
            console.error('Error fetching location information:', error.message);
            // throw new Error(error.response?.data?.message || error.message);
        }
    };

    const getCategories = async () => {
        try {
            const { data } = await axios.get(
                props.userType !== userType['CARWASH-OWNER']
                    ? LocationApi.ADMIN_GET_CATEGORIES
                    : LocationApi.GET_CATEGORIES
            );
            if (data.error === 0) {
                const categoryList = Array.isArray(data?.data) ? data.data : [];
                const transformedArray: DeviceOption[] = categoryList
                    .map((item: any) => ({
                        label: item.washCategoryName ?? '',
                        value: item.washCategoryId ?? 0,
                    }))
                    .filter((item: DeviceOption) => item.label && item.value !== undefined && item.value !== null);

                if (transformedArray.length > 0) {
                    const hasBoth = transformedArray.some(
                        (item) => String(item.label).toLowerCase() === 'both'
                    );
                    if (!hasBoth) {
                        transformedArray.push({ label: 'Both', value: 0 });
                    }
                }

                setCategoryOptions(transformedArray);
            } else {
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            console.error('Error fetching categories:', error.message);
        }
    };

    // Fetch company information on component mount
    useEffect(() => {
        getCompanyAddress();
        getLocation();
        getEquipment();  // Location Details
        getDevices();
        getCategories();
    }, []);
        
    // Access the context
    const { updateData } = useOutletContext<AppContextType>();

    // To display heading
    useEffect(() => {
        handleUpdate()
    }, [current_locationName])

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: equipment_id === 0 ? "Add Bay" : "Edit Bay", url: props.userType !== userType['CARWASH-OWNER'] ? `/superadmin/${pageURLs.equipment_listing}/${current_location_id}/${current_carwash_id}?page=${page}` : `/${pageURLs.equipment_listing}/${current_location_id}?page=${page}`, showBackButton: true, showMainMenu: true, activeLink: props.userType !== userType['CARWASH-OWNER'] ? "manage-car-wash" : "manage-car-wash-locations" });
    };

    // Convert ConnectivityType enum to an array of options
    const connectivityOptions = [
        { label: 'Select', value: 0 }, // First option with value 0
        ...Object.keys(ConnectivityType).filter(key => !isNaN(Number(key))).map(key => ({
            label: ConnectivityType[key as keyof typeof ConnectivityType],
            value: Number(key)
        }))
    ];

    // Convert EquipmentType enum to an array of options
    const equipmentOptions = [
        { label: 'Select', value: 0 }, // First option with value 0
        ...Object.keys(EquipmentType).filter(key => !isNaN(Number(key))).map(key => ({
            label: EquipmentType[key as keyof typeof EquipmentType],
            value: Number(key)
        }))
    ];


    const bayNumberOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => ({
        label: num,
        value: num
        }));
        
    const relayNumberOptions =  [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => ({
        label: num,
        value: num
        }));

    const lprUsageTypeOptions = [
        { label: 'Select', value: 0 },
        { label: 'Only for Car number validation', value: 1 },
        { label: 'For Handsfree usage', value: 2 },
    ];


    //State to handle form data
    const [formData, setFormData] = useState<EquipmentData>({
        baynumber: 0,
        paystation: '',
        internetconnectivity: 0,
        equipmenttype: 0,
        deviceid: 0,
        bayName:'',
        relayNumber:0,
        isPulse:false,
        isPax: false,
        isLpr: false,
        isComboWash: false,
        washCategoryId: null,
        isUpsell: false,
        isBuyUpEnabled: false,
        lprUsageType: null,
        secPerPulse:0,
        minPulsesToStart:0,
        minRuntime:0
    });

    const showCategory = locationAllowsComboWash && equipmentTypeSupportsComboWash(formData.equipmenttype);

    
    const setIsPulse = async (e: any) => {
    if (e.target.name === 'isPulse') {
        if (e.target.checked === true) {
            setFormData({
                ...formData,
                [e.target.name]: e.target.checked,
            });
        } else {
            setErrors({
        ...errors,
        secPerPulse: '',
        minPulsesToStart: '',
        minRuntime: ''
      });
            setFormData({
                ...formData,
                [e.target.name]: e.target.checked,
                ['isPulse']: false,
                ['secPerPulse']: 0,
                ['minPulsesToStart']: 0,
                ['minRuntime']:0
            });
        }
    } 
    }

    const setEquipmentBooleanSwitch = (e: any) => {
        const { name, checked } = e.target;
        if (name === 'isPax' || name === 'isLpr' || name === 'isUpsell' || name === 'isBuyUpEnabled') {
            setFormData((prev) => ({
                ...prev,
                [name]: checked,
                ...(name === 'isLpr' && !checked ? { lprUsageType: null } : {}),
            }));
        }
    };

    // To get equipment
    const getEquipment = async () => {
        if(equipment_id !== 0)
        {
            try {
                setLoading(true); // Show loader
                let param = '';
                param += equipment_id;
                const { data } = (props.userType !== userType['CARWASH-OWNER']) ? await axios.get(EquipmentApi.GET_SINGLE_EQUIPMENT + param) : await axios.get(EquipmentApi.CARWASH_OWNER_GET_SINGLE_EQUIPMENT + param)
                setFormData({
                    baynumber:data?.data?.bayNumber,
                    paystation: data?.data?.PayStationBrand,
                    internetconnectivity: data?.data?.connectivityType,
                    equipmenttype: data?.data?.equipmentType,
                    deviceid: data?.data?.WashBayDeviceID,
                    bayName:data?.data?.bayName,
                    relayNumber:data?.data?.relayNumber,
                    isPulse: Boolean(data?.data?.isPulse),
                    isPax: Boolean(data?.data?.isPax),
                    isLpr: Boolean(data?.data?.isLPR ?? data?.data?.isLpr),
                    isComboWash: locationAllowsComboWash && equipmentTypeSupportsComboWash(data?.data?.equipmentType),
                    washCategoryId: (() => {
                        const raw =
                            data?.data?.washCategory ??
                            data?.data?.washCategoryId ??
                            data?.data?.WashCategoryID ??
                            data?.data?.categoryId;
                        if (raw === null || raw === undefined || raw === '') return null;
                        const n = Number(raw);
                        return Number.isNaN(n) ? null : n;
                    })(),
                    isUpsell: Boolean(data?.data?.isUpsell ?? data?.data?.isUpsellSupported),
                    isBuyUpEnabled: Boolean(data?.data?.isBuyUpEnabled),
                    lprUsageType: (() => {
                        const raw = data?.data?.lprMode ?? data?.data?.lprUsageType;
                        if (raw === null || raw === undefined || raw === '') return null;
                        const n = Number(raw);
                        return Number.isNaN(n) ? null : n;
                    })(),
                    secPerPulse:data?.data?.secPerPulse,
                    minPulsesToStart:data?.data?.minPulses,
                    minRuntime:data?.data?.secPerPulse,
                });
                
            } catch (error: any) {
                throw new Error(error.response?.data?.message || error.message);
            } finally {
                setLoading(false); // Hide loader
            }
           
        }
    }

    //State to handle errors
    const [errors, setErrors] = useState<any>({});

    const refs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>({});

    const getBrandLabel = (type: number): string => {
        switch (type) {
            case EquipmentType['Self Serve']:
                return 'Brand of Self Serve Timer';
            case EquipmentType['Vacuum']:
                return 'Brand of Vacuum';
            case EquipmentType['Dog Wash']:
                return 'Brand of Dog Wash';
            case EquipmentType['In Bay']:
            case EquipmentType['Tunnel']:
            default:
                return 'Brand of In Bay or Tunnel Controller';
        }
    };

    /** Pulse hidden for In Bay only; LPR for In Bay and Tunnel — keep form state consistent when bay type changes. */
    const applyEquipmentTypeChange = (prev: EquipmentData, nextType: number): EquipmentData => {
        if (equipmentTypeHidesPulse(nextType)) {
            return {
                ...prev,
                equipmenttype: nextType,
                isPulse: false,
                secPerPulse: 0,
                minPulsesToStart: 0,
                minRuntime: 0,
            };
        }
        // Keep isLpr / lprUsageType only when bay type is In Bay or Tunnel.
        if (equipmentTypeSupportsLpr(nextType)) {
            return {
                ...prev,
                equipmenttype: nextType,
                isComboWash: locationAllowsComboWash && equipmentTypeSupportsComboWash(nextType),
            };
        }
        return {
            ...prev,
            equipmenttype: nextType,
            isLpr: false,
            isComboWash: false,
            washCategoryId: null,
            isUpsell: false,
            lprUsageType: null,
        };
    };

    // On input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (name === 'equipmenttype') {
            const nextType = value === '' ? 0 : Number(value);
            if (Number.isNaN(nextType)) return;
            if (equipmentTypeHidesPulse(nextType)) {
                setErrors({
                    ...errors,
                    equipmenttype: '',
                    secPerPulse: '',
                    minPulsesToStart: '',
                    minRuntime: '',
                    ...(!equipmentTypeSupportsComboWash(nextType) ? { washCategoryId: '' } : {}),
                });
            } else if (!equipmentTypeSupportsLpr(nextType)) {
                setErrors({
                    ...errors,
                    equipmenttype: '',
                    lprUsageType: '',
                    washCategoryId: '',
                });
            } else {
                setErrors({
                    ...errors,
                    equipmenttype: '',
                });
            }
            setFormData((prev) => applyEquipmentTypeChange(prev, nextType));
            return;
        }

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

       const numericFields = ["secPerPulse", "minPulsesToStart", "minRuntime"];
          // If field is numeric
    if (numericFields.includes(name)) {
        // Only allow digits, max 4 characters
        if (!/^\d{0,4}$/.test(value)) return;

        setFormData(prev => ({
            ...prev,
            [name]: value === "" ? "" : Number(value),
        }));
        return;
    }

    // For non-numeric fields
    setFormData(prev => ({
        ...prev,
        [name]: value,
    }));


    };

    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption?.value ?? '';

        if (name === 'equipmenttype') {
            const nextType = Number(value);
            if (equipmentTypeHidesPulse(nextType)) {
                setErrors({
                    ...errors,
                    equipmenttype: '',
                    secPerPulse: '',
                    minPulsesToStart: '',
                    minRuntime: '',
                    ...(!equipmentTypeSupportsComboWash(nextType) ? { washCategoryId: '' } : {}),
                });
            } else if (!equipmentTypeSupportsLpr(nextType)) {
                setErrors({
                    ...errors,
                    equipmenttype: '',
                    lprUsageType: '',
                    washCategoryId: '',
                });
            } else {
                setErrors({
                    ...errors,
                    equipmenttype: '',
                });
            }
            setFormData((prev) => applyEquipmentTypeChange(prev, nextType));
            return;
        }

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        let newFormData: EquipmentData =
            name === 'lprUsageType'
                ? {
                      ...formData,
                      lprUsageType:
                          value === 0 || value === '0' || value === '' ? null : Number(value),
                  }
                : name === 'washCategoryId'
                ? {
                      ...formData,
                      washCategoryId:
                          value === '' || value === undefined || value === null ? null : Number(value),
                  }
                : {
                      ...formData,
                      [name]: value,
                  };

        setFormData(newFormData);


    };

    //To validate form on clicking finish
    const validateForm = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if(equipment_id === 0)
            {
                try {
                        setLoading(true); // Show loader
                        await equipmentInfoSchema.validate({ ...formData, isComboWash: showCategory }, { abortEarly: false });
                        try {
                            const equipmentRequestBody = {
                                locationId: current_location_id,
                                bayNumber: formData?.baynumber,
                                payStationBrand: formData?.paystation,
                                connectivityType: formData?.internetconnectivity,
                                equipmentType: formData?.equipmenttype,
                                deviceId: formData?.deviceid,
                                bayName:formData?.bayName,
                                relayNumber:formData?.relayNumber,
                                isPulse:formData.isPulse,
                                isPax: formData.isPax ? 1 : 0,
                                isLPR: formData.isLpr ? 1 : 0,
                                isComboWash: Boolean(showCategory),
                                washCategory: showCategory
                                    ? formData.washCategoryId != null
                                        ? Number(formData.washCategoryId)
                                        : null
                                    : null,
                                isUpsell: Boolean(equipmentTypeSupportsComboWash(formData.equipmenttype) && formData.isUpsell),
                                isBuyUpEnabled: Boolean(formData.isBuyUpEnabled),
                                lprMode: formData.isLpr
                                    ? formData.lprUsageType != null
                                        ? Number(formData.lprUsageType)
                                        : null
                                    : null,
                                secPerPulse:formData.secPerPulse,
                                minPulsesToStart:formData.minPulsesToStart,
                                minRuntime:Number((Number(formData.secPerPulse || 0) * Number(formData.minPulsesToStart || 0)/60).toFixed(2)),
                            };
                            const responseLocationEquipmentUpdate = (props.userType !== userType['CARWASH-OWNER']) ? await axios.post(LocationApi.ADD_LOCATION_EQUIPMENT, equipmentRequestBody) : await axios.post(LocationApi.CARWASH_OWNER_ADD_LOCATION_EQUIPMENT, equipmentRequestBody);
                            if (responseLocationEquipmentUpdate?.data?.error === 0) {
                                setErrors({});
                                setShowEquipmentSuccesspoup(true);
                                setFormData({
                                    baynumber: 0,
                                    paystation: '',
                                    internetconnectivity: 0,
                                    equipmenttype: 0,
                                    deviceid: 0,
                                    bayName:'',
                                    isPulse:false,
                                    isPax: false,
                                    isLpr: false,
                                    isComboWash: false,
                                    washCategoryId: null,
                                    isUpsell: false,
                                    isBuyUpEnabled: false,
                                    lprUsageType: null,
                                    secPerPulse:0,
                                    minPulsesToStart:0,
                                    minRuntime:0,
                                });
                            }
                            else {
                                toaster.toastError(responseLocationEquipmentUpdate?.data?.message);
                            }
                        } catch (error : any) {
                            throw new Error(error?.response?.data?.message || error?.message);
                        }
                        
                } catch (error) {
                    const newErrors: any = {};

                    // If validation fails on submit, then errors are shown
                   if (error instanceof Yup.ValidationError) {
                                   error.inner.forEach((err) => {
                                       if (err.path) {
                                         newErrors[err.path as keyof typeof formData] = err.message;
                                       } else {
                                         // Form-level error 
                                         toaster.toastError(err.message)
                                       }
                                   })
                                   setErrors(newErrors);
                               }

                    // Focus on the first invalid field
                    if (formRef.current) {
                        const firstInvalidField = Object.keys(newErrors)[0];
                        const invalidElement = formRef.current.querySelector<HTMLInputElement>(`[name="${firstInvalidField}"]`);
                        if (invalidElement) {
                            invalidElement.focus();
                        }
                    }
                    // Focus on the first field with an error
                    if (Object.keys(newErrors).length > 0) {
                        const firstErrorField = Object.keys(newErrors)[0];
                        const element = refs.current[firstErrorField];
                        if (element) {
                            element.focus();
                        }
                    }

                    return false
                } finally {
                    setLoading(false); // Hide loader
                }

            }
            else
            {

                try {
                    setLoading(true); // Show loader
                    console.log(formData)
                    await equipmentInfoSchema.validate({ ...formData, isComboWash: showCategory }, { abortEarly: false });
                    try {
                        const equipmentRequestBody = {
                            bayId: equipment_id,
                            bayNumber: Number(formData?.baynumber),
                            payStationBrand: formData?.paystation,
                            connectivityType: formData?.internetconnectivity,
                            equipmentType: formData?.equipmenttype,
                            deviceId: formData?.deviceid,
                            locationId: current_location_id,
                            bayName:formData?.bayName,
                            relayNumber:formData?.relayNumber,
                            isPulse:formData.isPulse,
                            isPax: formData.isPax ? 1 : 0,
                            isLPR: formData.isLpr ? 1 : 0,
                            isComboWash: Boolean(showCategory),
                            washCategory: showCategory
                                ? formData.washCategoryId != null
                                    ? Number(formData.washCategoryId)
                                    : null
                                : null,
                            isUpsell: Boolean(equipmentTypeSupportsComboWash(formData.equipmenttype) && formData.isUpsell),
                            isBuyUpEnabled: Boolean(formData.isBuyUpEnabled),
                            lprMode: formData.isLpr
                                ? formData.lprUsageType != null
                                    ? Number(formData.lprUsageType)
                                    : null
                                : null,
                            secPerPulse:formData.secPerPulse,
                            minPulsesToStart:formData.minPulsesToStart,
                            minRuntime:Number((Number(formData.secPerPulse || 0) * Number(formData.minPulsesToStart || 0)/60).toFixed(2))
                        };
                        const responseLocationEquipmentUpdate = (props.userType !== userType['CARWASH-OWNER']) ? await axios.post(EquipmentApi.UPDATE_EQUIPMENT, equipmentRequestBody) : await axios.post(EquipmentApi.CARWASH_OWNER_UPDATE_EQUIPMENT, equipmentRequestBody);
                        if (responseLocationEquipmentUpdate?.data?.error === 0) {
                            setErrors({});
                            setFormData({
                                baynumber: 0,
                                paystation: '',
                                internetconnectivity: 0,
                                equipmenttype: 0,
                                deviceid: 0,
                                bayName:'',
                                isPulse:false,
                                isPax: false,
                                isLpr: false,
                                isComboWash: false,
                                washCategoryId: null,
                                isUpsell: false,
                                isBuyUpEnabled: false,
                                lprUsageType: null,
                                secPerPulse:0,
                                minPulsesToStart:0,
                                minRuntime:0,
                            });
                            toaster.toastSuccess(responseLocationEquipmentUpdate?.data?.message)
                            if (props.userType !== userType['CARWASH-OWNER']) {
                                navigate(`/superadmin/${pageURLs.equipment_listing}/${current_location_id}/${current_carwash_id}?page=${page}`);
                            } else {
                                navigate(`/${pageURLs.equipment_listing}/${current_location_id}?page=${page}`);
                            }
                        }
                        else {
                            toaster.toastError(responseLocationEquipmentUpdate?.data?.message);
                        }
                    } catch (error : any) {
                        throw new Error(error?.response?.data?.message || error?.message);
                    }
                    
                } catch (error) {
                    const newErrors: any = {};
        
                    // If validation fails on submit, then errors are shown
                    if (error instanceof Yup.ValidationError) {
                                    error.inner.forEach((err) => {
                                        if (err.path) {
                                          newErrors[err.path as keyof typeof formData] = err.message;
                                        } else {
                                          // Form-level error 
                                          toaster.toastError(err.message)
                                        }
                                    })
                                    setErrors(newErrors);
                                }
        
                    // Focus on the first invalid field
                    if (formRef.current) {
                        const firstInvalidField = Object.keys(newErrors)[0];
                        const invalidElement = formRef.current.querySelector<HTMLInputElement>(`[name="${firstInvalidField}"]`);
                        if (invalidElement) {
                            invalidElement.focus();
                        }
                    }
                    // Focus on the first field with an error
                    if (Object.keys(newErrors).length > 0) {
                        const firstErrorField = Object.keys(newErrors)[0];
                        const element = refs.current[firstErrorField];
                        if (element) {
                            element.focus();
                        }
                    }
        
                    return false
                } finally {
                    setLoading(false); // Hide loader
                }
               
            }
    };

        //Hide ppopup
        const hideEquipmentAddModal = () => {
            setShowEquipmentSuccesspoup(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        const NoMoreEquipmentAction = () => {
            setShowEquipmentSuccesspoup(false);
            if(props.userType !== userType['CARWASH-OWNER'])
            {
            navigate(`/superadmin/${pageURLs.equipment_listing}/${current_location_id}/${current_carwash_id}?page=${page}`);
            }
            else
            {
                navigate(`/${pageURLs.equipment_listing}/${current_location_id}?page=${page}`);
            }
        }

        // Navigate accordung to url
        const itemClick = async (url: string) => {
            navigate(url);
        }

        //Get Devices
        const getDevices = async () => {
            try {
                setLoading(true); // Hide loader
                const { data } = (props.userType !== userType['CARWASH-OWNER']) ? await axios.get(EquipmentApi.DEVICE_LISTING_API+current_carwash_id+'/'+equipment_id) : await axios.get(EquipmentApi.CARWASH_OWNER_DEVICE_LISTING_API+equipment_id);
                if (data.error === 0) {
                    setLoading(false); // Hide loader
                    const device_list: DeviceItem[] = data?.data
                    
                    // Transform the array
                    const transformedArray: DeviceOption[] = device_list.map(item => ({
                        label: item.deviceName,
                        value: item.washBayDeviceID,
                    }));
    
                    setdeviceOptions(transformedArray)
                }
                else {
                    // show error message
                    toaster.toastError(data.message);
                }
            } catch (error: any) {
                setLoading(false); // Hide loader
                console.log(error)
                throw new Error(error?.response?.data?.message || error?.message);
            }

        }

    return (

        <div className='manage-subscription-container'>
            <Container>
                <Breadcrumb>
                    {props.userType !== userType['CARWASH-OWNER'] ? (
                        <>
                            <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.manage_carwash}`) }}>Manage Car Washes</Breadcrumb.Item>
                            <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.carwash_locations}/${current_carwash_id}`) }}>{company.companyName}</Breadcrumb.Item>
                            <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.equipment_listing}/${current_location_id}/${current_carwash_id}?page=${page}`) }}>{current_locationName}</Breadcrumb.Item>
                            <Breadcrumb.Item active>{equipment_id === 0 ? "Add Bay" : "Edit Bay"}</Breadcrumb.Item>
                        </>
                    ) : (
                        <>
                             <Breadcrumb.Item onClick={() => { itemClick(`/${pageURLs.carwash_locations}`) }}>Manage Car Wash Locations</Breadcrumb.Item>
                            <Breadcrumb.Item onClick={() => { itemClick(`/${pageURLs.equipment_listing}/${current_location_id}?page=${page}`) }}>{current_locationName}</Breadcrumb.Item>
                            <Breadcrumb.Item active>{equipment_id === 0 ? "Add Bay" : "Edit Bay"}</Breadcrumb.Item>
                        </>
                    )}
                </Breadcrumb>
                <form onSubmit={validateForm} ref={formRef}>
                    <div className='cw-form-outer-wrap'>
                        <div className="cw-form-container">
                            <div className="form-inner mx-auto">

                                <h5>Car Wash Details</h5>
                                <Row>
                                <Col md={6}>
                                <CWSelect
                                        id="equipmenttype"
                                        name="equipmenttype"
                                        options={equipmentOptions}
                                        label="Select Bay Type"
                                        errorMessage={errors.equipmenttype && errors.equipmenttype}
                                        dropdownGap={0}
                                        dropdownPosition="auto"
                                        values={equipmentOptions.filter(option => option.value === formData.equipmenttype)}
                                        onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'equipmenttype')}
                                        placeholder="Select Bay Type"
                                        multi={false}
                                        searchable={false}
                                        isMandatory
                                    />
                                </Col>
                                <Col md={6}>
                                    <CWSelect
                                        id="baynumber"
                                        name="baynumber"
                                        options={bayNumberOptions}
                                        label="Select Bay Number"
                                        errorMessage={errors.baynumber && errors.baynumber}
                                        dropdownGap={0}
                                        dropdownPosition="auto"
                                        values={bayNumberOptions.filter(option => option.value === Number(formData.baynumber))}
                                        onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'baynumber')}
                                        placeholder="Select Bay Number"
                                        multi={false}
                                        searchable={false}
                                        isMandatory
                                    />
                                    </Col>
                                </Row>
                                         <Row>
                                    <Col md={6}>
                                    <CWInput
                                        id="paystation"
                                        name="paystation"
                                        type="text"
                                        label={getBrandLabel(formData.equipmenttype)}
                                        value={formData.paystation}
                                        onChange={handleChange}
                                        errorMessage={errors.paystation}
                                        maxLength={DEVICE_ID_MAX_LENGTH}
                                        ref={(el) => (refs.current.paystation = el)}
                                        isMandatory
                                    />
                                    </Col>
                                    <Col md={6}>

                                        <CWSelect
                                            id="internetconnectivity"
                                            name="internetconnectivity"
                                            options={connectivityOptions}
                                            label="Select Connectivity Type"
                                            errorMessage={errors.internetconnectivity && errors.internetconnectivity}
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            values={connectivityOptions.filter(option => option.value === formData.internetconnectivity)}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'internetconnectivity')}
                                            placeholder="Select Connectivity Type"
                                            multi={false}
                                            searchable={false}
                                            isMandatory
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                    <CWSelect
                                        id="deviceid"
                                        name="deviceid"
                                        options={deviceOptions}
                                        values={deviceOptions.filter(option => option.value === formData.deviceid)}
                                        label="Select Device"
                                        dropdownGap={0}
                                        dropdownPosition="auto"
                                        placeholder="Select"
                                        multi={false}
                                        isMandatory= {false}
                                        errorMessage={errors.state}
                                        onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'deviceid')}
                                        className="no-input"
                                    />
                                    </Col>
                                    <Col md={6}>
                                    <CWInput
                                    id="bayName"
                                    name="bayName"
                                    type="text"
                                    label="Bay Name"
                                    value={formData.bayName}
                                    onChange={handleChange}
                                    errorMessage={errors.bayName}
                                    maxLength={EQUIPMENT_NAME_MAX_LENGTH}
                                    ref={(el) => (refs.current.bayName = el)}
                                    autoFocus
                                    isMandatory
                                /> 
                                    </Col>
                                </Row>
                               {(formData.equipmenttype != EquipmentType['In Bay'] && formData.equipmenttype != EquipmentType['Tunnel']) ? (
                                    <Row>
                                    <Col md={6}>
                                    <CWSelect
                                        id="relayNumber"
                                        name="relayNumber"
                                        options={relayNumberOptions}
                                        label="Select Relay Number"
                                        errorMessage={errors.relayNumber && errors.relayNumber}
                                        dropdownGap={0}
                                        dropdownPosition="auto"
                                        values={relayNumberOptions.filter(option => option.value === Number(formData.relayNumber))}
                                        onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'relayNumber')}
                                        placeholder="Select Relay Number"
                                        multi={false}
                                        searchable={false}
                                        isMandatory
                                    />
                                    </Col>
                                    </Row>
                                ) : null}
                                <Row className="align-items-md-start">
                                    <Col md={6}>
                                        <div className="info-switch d-flex align-items-center mt-4">
                                            <h5 className="m-0 me-2">Is PAX Supported?</h5>
                                            <CWSwitch
                                                id="isPax"
                                                name="isPax"
                                                checked={formData.isPax}
                                                onChange={(e) => {
                                                    setEquipmentBooleanSwitch(e);
                                                }}
                                            />
                                        </div>
                                        <div className="info-switch d-flex align-items-center mt-4">
                                            <h5 className="m-0 me-2">Is Upsell Enabled?</h5>
                                            <CWSwitch
                                                id="isBuyUpEnabled"
                                                name="isBuyUpEnabled"
                                                checked={Boolean(formData.isBuyUpEnabled)}
                                                onChange={(e) => {
                                                    setEquipmentBooleanSwitch(e);
                                                }}
                                            />
                                        </div>
                                        {showCategory ? (
                                            <div className="mt-4">
                                                <CWSelect
                                                    id="washCategoryId"
                                                    name="washCategoryId"
                                                    options={categoryOptions}
                                                    label="Category"
                                                    errorMessage={errors.washCategoryId && errors.washCategoryId}
                                                    dropdownGap={0}
                                                    dropdownPosition="auto"
                                                    values={categoryOptions.filter(
                                                        (option) =>
                                                            formData.washCategoryId !== null &&
                                                            formData.washCategoryId !== undefined &&
                                                            option.value === Number(formData.washCategoryId)
                                                    )}
                                                    onChange={(selectedOptions) =>
                                                        handleSelectChange(selectedOptions[0], 'washCategoryId')
                                                    }
                                                    placeholder="Select Category"
                                                    multi={false}
                                                    searchable={false}
                                                    isMandatory
                                                />
                                            </div>
                                        ) : null}
                                    </Col>
                                    <Col md={6}>
                                        {!equipmentTypeHidesPulse(formData.equipmenttype) ? (
                                            <div className="info-switch d-flex align-items-center mt-4 mb-4">
                                                <h5 className="m-0 me-2">Is Pulse Supported?</h5>
                                                <CWSwitch
                                                    id="isPulse"
                                                    name="isPulse"
                                                    checked={formData.isPulse}
                                                    onChange={(e) => {
                                                        setIsPulse(e);
                                                    }}
                                                />
                                            </div>
                                        ) : null}
                                        {equipmentTypeSupportsLpr(formData.equipmenttype) ? (
                                            <>
                                                <div className="info-switch d-flex align-items-center mt-4">
                                                    <h5 className="m-0 me-2">Is LPR Supported?</h5>
                                                    <CWSwitch
                                                        id="isLpr"
                                                        name="isLpr"
                                                        checked={formData.isLpr}
                                                        onChange={(e) => {
                                                            setEquipmentBooleanSwitch(e);
                                                        }}
                                                    />
                                                </div>
                                                {formData.isLpr ? (
                                                    <div className="mt-3">
                                                        <CWSelect
                                                            id="lprUsageType"
                                                            name="lprUsageType"
                                                            options={lprUsageTypeOptions}
                                                            label="LPR usage"
                                                            errorMessage={errors.lprUsageType && errors.lprUsageType}
                                                            dropdownGap={0}
                                                            dropdownPosition="auto"
                                                            values={lprUsageTypeOptions.filter(
                                                                (option) =>
                                                                    option.value === (formData.lprUsageType ?? 0)
                                                            )}
                                                            onChange={(selectedOptions) =>
                                                                handleSelectChange(selectedOptions[0], 'lprUsageType')
                                                            }
                                                            placeholder="Select LPR usage"
                                                            multi={false}
                                                            searchable={false}
                                                            isMandatory
                                                        />
                                                    </div>
                                                ) : null}
                                            </>
                                        ) : null}
                                    </Col>
                                </Row>
                                  
                                    {formData.isPulse ? <Row>
                                        <Col md={6}>
                                            <CWInput
                                                id="secPerPulse"
                                                name="secPerPulse"
                                                label="Duration per Pulse (Seconds)"
                                                maxLength={4}
                                                type="text"
                                                pattern="\d*"
                                                isMandatory
                                                value={formData.secPerPulse}
                                                errorMessage={errors.secPerPulse}
                                                onChange={handleChange}
                                            />
                                        </Col>
                                        <Col md={6}>
                                                <CWInput
                                                id="minPulsesToStart"
                                                name="minPulsesToStart"
                                                label={`Minimum Pulses Required to Start ${formData.equipmenttype==EquipmentType['Vacuum'] ? "vacuum" : "wash"}`}
                                                maxLength={4}
                                                isMandatory
                                                type="text"
                                                pattern="\d*"
                                                value={formData.minPulsesToStart}
                                                errorMessage={errors.minPulsesToStart}
                                                onChange={handleChange}
                                            />
                                        </Col>
                                       
                                         <Col md={6}>
                                                <CWInput
                                                id="minRuntime"
                                                name="minRuntime"
                                                label="Minimum Run Time (Minutes)"
                                                maxLength={WASH_NUMBER_MAX_LENGTH}
                                                  type='number'
                                                isMandatory
                                                value={
                                                    Number((Number(formData.secPerPulse || 0) * Number(formData.minPulsesToStart || 0)/60).toFixed(2))
                                                    }
                                                    readOnly
                                                // errorMessage={errors.maxVacuumTime}
                                                onChange={handleChange}
                                            />
                                        </Col>
                                    </Row> : ''}

                            </div>

                        </div>

                        <div className="btn-sec d-flex justify-content-end">
                            {
                            props.userType !== userType['CARWASH-OWNER'] ? (
                                <Button title="Cancel" variant="secondary" className="secondary-btn" onClick={() => navigate(`/superadmin/${pageURLs.equipment_listing}/${current_location_id}/${current_carwash_id}?page=${page}`)}>Cancel</Button>
                            ) : (
                                <Button title="Cancel" variant="secondary" className="secondary-btn" onClick={() => navigate(`/${pageURLs.equipment_listing}/${current_location_id}?page=${page}`)}>Cancel</Button>
                            )
                            }
                            <Button title="Save Bay" variant="primary" className="primary-btn" type='submit' >Save Bay</Button>
                        </div>
                    </div>
                </form>
                <Modal
                    show={showEquipmentSuccesspoup}
                    onHide={hideEquipmentAddModal}
                    centered
                    className='add-modal'
                >
                    <Modal.Body className='text-center'>
                        <h5 className='f-bold text-center'>Bay added successfully! Do you want to add another?</h5>
                        <div className="btn-sec d-grid">
                            <Button variant="light" className="secondary-btn" onClick={NoMoreEquipmentAction}>Not Now</Button>
                            <Button variant="primary" className='primary-btn btn-main m-0' onClick={hideEquipmentAddModal}>Yes</Button>
                        </div>
                    </Modal.Body>
                </Modal>
            </Container >
            {loading && <SmallLoader />}
        </div >
    )
}

export default AddEquipment;
